import { ArrowLeft, ArrowRight, FileText, Flag, FlagOff, Timer } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EXAM_BY_ID } from "../data/exams";
import { QUESTION_BY_ID } from "../data/questions";
import { QuestionView } from "../components/QuestionView";
import { Empty, ProgressBar, useDocumentTitle } from "../components/ui";
import { formatDuration } from "../lib/dates";
import { addAttempt, recordAnswer } from "../lib/progress";
import { isCorrect } from "../lib/scoring";
import { displayTitle, gradeSession, isInstantFeedback, sessionStore, useSession, type Session } from "../lib/session";
import { progressStore } from "../lib/store";
import { useLang, useT } from "../i18n";

function finishSession(s: Session, timedOut = false): string | null {
  const now = Date.now();
  let toGrade = s;
  if (isInstantFeedback(s.kind)) {
    // Luyện tập: chỉ chấm các câu đã nộp; câu đã được ghi nhận khi nộp.
    const done = s.questionIds.filter((id) => s.revealed[id]);
    if (!done.length) {
      sessionStore.set(null);
      return null;
    }
    toGrade = { ...s, questionIds: done };
  }
  const attempt = gradeSession(toGrade, now, timedOut);
  progressStore.update((p) => {
    let next = p;
    if (!isInstantFeedback(s.kind)) {
      for (const id of s.questionIds) {
        const q = QUESTION_BY_ID.get(id);
        if (q) next = recordAnswer(next, id, isCorrect(s.answers[id] ?? [], q.answer), now);
      }
    }
    return addAttempt(next, attempt);
  });
  sessionStore.set(null);
  return attempt.id;
}

function useCountdown(s: Session | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  const timed = !!s?.durationMs;
  useEffect(() => {
    if (!timed) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [timed]);
  if (!s?.durationMs) return null;
  return s.startedAt + s.durationMs - now;
}

export function SessionPage() {
  const s = useSession();
  const navigate = useNavigate();
  const remaining = useCountdown(s);
  const finishedRef = useRef(false);
  const t = useT();
  const lang = useLang();
  useDocumentTitle(s ? displayTitle(s, lang) : t("Phiên luyện tập", "Practice session"));

  const finish = useCallback(
    (timedOut = false) => {
      const cur = sessionStore.get();
      if (!cur || finishedRef.current) return;
      finishedRef.current = true;
      const id = finishSession(cur, timedOut);
      navigate(id ? `/result/${id}` : "/", { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    if (remaining !== null && remaining <= 0) finish(true);
  }, [remaining, finish]);

  const qid = s?.questionIds[s.index];
  const q = qid ? QUESTION_BY_ID.get(qid) : undefined;
  const instant = s ? isInstantFeedback(s.kind) : false;
  const revealed = !!(qid && s?.revealed[qid]);
  const selected = (qid && s?.answers[qid]) || [];
  const isLast = s ? s.index === s.questionIds.length - 1 : false;

  const setAnswer = useCallback((next: number[]) => {
    sessionStore.update((cur) => {
      const id = cur.questionIds[cur.index];
      if (!id || cur.revealed[id]) return cur;
      return { ...cur, answers: { ...cur.answers, [id]: next } };
    });
  }, []);

  const go = useCallback((i: number) => {
    sessionStore.update((cur) => ({ ...cur, index: Math.max(0, Math.min(cur.questionIds.length - 1, i)) }));
  }, []);

  const submit = useCallback(() => {
    const cur = sessionStore.get();
    if (!cur) return;
    const id = cur.questionIds[cur.index];
    const question = id ? QUESTION_BY_ID.get(id) : undefined;
    if (!id || !question || cur.revealed[id] || !(cur.answers[id]?.length)) return;
    const ok = isCorrect(cur.answers[id] ?? [], question.answer);
    progressStore.update((p) => recordAnswer(p, id, ok, Date.now()));
    sessionStore.set({ ...cur, revealed: { ...cur.revealed, [id]: true } });
  }, []);

  const toggleFlag = useCallback(() => {
    sessionStore.update((cur) => {
      const id = cur.questionIds[cur.index];
      if (!id) return cur;
      return { ...cur, flagged: cur.flagged.includes(id) ? cur.flagged.filter((x) => x !== id) : [...cur.flagged, id] };
    });
  }, []);

  const confirmFinish = useCallback(() => {
    const cur = sessionStore.get();
    if (!cur) return;
    if (!isInstantFeedback(cur.kind)) {
      const unanswered = cur.questionIds.filter((id) => !cur.answers[id]?.length).length;
      const msg = unanswered
        ? t(`Còn ${unanswered} câu chưa trả lời (tính là sai). Nộp bài?`, `${unanswered} question(s) unanswered (counted as wrong). Submit?`)
        : t("Nộp bài và xem kết quả?", "Submit and see your results?");
      if (!window.confirm(msg)) return;
    }
    finish();
  }, [finish, t]);

  // Phím tắt
  useEffect(() => {
    if (!s || !q) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      const k = e.key.toLowerCase();
      const order = s.optionOrder[q.id] ?? [];
      const pos = "123456".indexOf(k) >= 0 ? "123456".indexOf(k) : "abcdef".indexOf(k);
      if (pos >= 0 && pos < order.length && !revealed) {
        const orig = order[pos] as number;
        if (q.answer.length > 1) setAnswer(selected.includes(orig) ? selected.filter((x) => x !== orig) : [...selected, orig]);
        else setAnswer([orig]);
        e.preventDefault();
      } else if (k === "enter") {
        e.preventDefault();
        if (instant) {
          if (!revealed) submit();
          else if (isLast) finish();
          else go(s.index + 1);
        } else if (!isLast) go(s.index + 1);
      } else if (k === "arrowright" && (!instant || revealed)) go(s.index + 1);
      else if (k === "arrowleft" && !instant) go(s.index - 1);
      else if (k === "f" && !instant) toggleFlag();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [s, q, revealed, selected, instant, isLast, setAnswer, submit, go, finish, toggleFlag]);

  if (!s || !q) {
    return (
      <Empty icon={FileText} title={t("Không có phiên nào đang diễn ra", "No session in progress")}>
        <p>{t("Chọn một đề để luyện tập hoặc thi thử.", "Pick an exam to practice or take a mock.")}</p>
        <div className="row" style={{ justifyContent: "center" }}>
          <Link className="btn primary" to="/exams">{t("Chọn đề thi", "Choose an exam")}</Link>
          <Link className="btn" to="/review">{t("Ôn tập", "Review")}</Link>
        </div>
      </Empty>
    );
  }

  const scenario = q.scenario ? EXAM_BY_ID[q.exam].scenarios?.find((x) => x.id === q.scenario) : undefined;
  const answeredCount = s.questionIds.filter((id) => (instant ? s.revealed[id] : s.answers[id]?.length)).length;
  const lowTime = remaining !== null && remaining < 5 * 60_000;

  return (
    <div className="session-layout">
      <div className="card">
        <div className="row between" style={{ marginBottom: "0.75rem" }}>
          <div>
            <div className="small muted">{displayTitle(s, lang)}</div>
            <strong>{t("Câu", "Question")} {s.index + 1} / {s.questionIds.length}</strong>
          </div>
          {remaining !== null && (
            <div className={`timer${lowTime ? " low" : ""}`} role="timer" aria-live={lowTime ? "polite" : "off"} aria-label={t("Thời gian còn lại", "Time remaining")}>
              <Timer className="ico" size={18} aria-hidden="true" /> {formatDuration(remaining)}
            </div>
          )}
        </div>
        <ProgressBar value={(answeredCount / s.questionIds.length) * 100} label={t("Tiến độ phiên", "Session progress")} />
        <div className="mt" />

        {scenario && (
          <div className="scenario-box" lang="en">
            <strong>Scenario: {scenario.title.en}.</strong> {scenario.context.en}
          </div>
        )}

        <QuestionView
          key={q.id}
          question={q}
          order={s.optionOrder[q.id] ?? q.options.map((_, i) => i)}
          selected={selected}
          onChange={setAnswer}
          revealed={instant && revealed}
        />

        <div className="row between mt">
          {instant ? (
            <>
              <button type="button" className="btn ghost" onClick={confirmFinish}>{t("Kết thúc", "End")}</button>
              {!revealed ? (
                <button type="button" className="btn primary" onClick={submit} disabled={!selected.length}>
                  {t("Kiểm tra", "Check")} <span className="kbd">Enter</span>
                </button>
              ) : isLast ? (
                <button type="button" className="btn primary" onClick={() => finish()}>{t("Xem kết quả", "See results")}</button>
              ) : (
                <button type="button" className="btn primary" onClick={() => go(s.index + 1)} autoFocus>
                  {t("Câu tiếp", "Next")} <span className="kbd">Enter</span>
                </button>
              )}
            </>
          ) : (
            <>
              <div className="row">
                <button type="button" className="btn" onClick={() => go(s.index - 1)} disabled={s.index === 0}><ArrowLeft className="ico" size={16} aria-hidden="true" /> {t("Trước", "Previous")}</button>
                <button type="button" className="btn ghost" onClick={toggleFlag} aria-pressed={s.flagged.includes(q.id)}>
                  {s.flagged.includes(q.id) ? <><FlagOff className="ico" size={16} aria-hidden="true" /> {t("Bỏ cờ", "Unflag")}</> : <><Flag className="ico" size={16} aria-hidden="true" /> {t("Gắn cờ", "Flag")}</>}
                </button>
              </div>
              {isLast ? (
                <button type="button" className="btn primary" onClick={confirmFinish}>{t("Nộp bài", "Submit")}</button>
              ) : (
                <button type="button" className="btn primary" onClick={() => go(s.index + 1)}>{t("Tiếp", "Next")} <ArrowRight className="ico" size={16} aria-hidden="true" /></button>
              )}
            </>
          )}
        </div>
        <p className="tiny muted mt mb-0">
          {t("Phím tắt", "Shortcuts")}: <span className="kbd">1</span>–<span className="kbd">4</span> {t("hoặc", "or")} <span className="kbd">A</span>–<span className="kbd">D</span> {t("chọn đáp án", "select")} ·{" "}
          <span className="kbd">Enter</span> {instant ? t("kiểm tra / câu tiếp", "check / next") : t("câu tiếp", "next")}
          {!instant && <> · <span className="kbd">←</span><span className="kbd">→</span> {t("di chuyển", "move")} · <span className="kbd">F</span> {t("gắn cờ", "flag")}</>}
        </p>
      </div>

      <aside className="card stack" aria-label={t("Danh sách câu hỏi", "Question list")}>
        <div className="row between">
          <strong>{t("Danh sách câu", "Questions")}</strong>
          <span className="small muted">{answeredCount}/{s.questionIds.length}</span>
        </div>
        <div className="qnav">
          {s.questionIds.map((id, i) => {
            const question = QUESTION_BY_ID.get(id);
            let cls = "";
            if (instant && s.revealed[id] && question) cls = isCorrect(s.answers[id] ?? [], question.answer) ? "right" : "wrong";
            else if (s.answers[id]?.length) cls = "answered";
            if (i === s.index) cls += " current";
            if (s.flagged.includes(id)) cls += " flagged";
            const canJump = !instant || s.revealed[id] || i === s.index;
            return (
              <button
                key={id}
                type="button"
                className={cls}
                onClick={() => canJump && go(i)}
                disabled={!canJump}
                aria-label={`${t("Câu", "Question")} ${i + 1}${s.flagged.includes(id) ? t(" (gắn cờ)", " (flagged)") : ""}`}
                aria-current={i === s.index ? "step" : undefined}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
        {!instant && (
          <>
            <p className="tiny muted mb-0">{t("Đậm = đã trả lời · chấm cam = gắn cờ", "Bold = answered · orange dot = flagged")}</p>
            <button type="button" className="btn primary" onClick={confirmFinish}>{t("Nộp bài", "Submit")}</button>
          </>
        )}
        <button
          type="button"
          className="btn ghost sm danger"
          onClick={() => {
            if (window.confirm(t("Hủy phiên này? Các câu đã kiểm tra vẫn được lưu vào tiến độ.", "Discard this session? Checked answers stay in your progress."))) {
              sessionStore.set(null);
              navigate("/");
            }
          }}
        >
          {t("Hủy phiên", "Discard session")}
        </button>
      </aside>
    </div>
  );
}
